/**
 * PowerBiAuthConsole — Power BI embed auth troubleshooting (3-step diagnostic).
 *
 * Collects the 4 credentials and verifies the embed auth chain in three steps:
 *   1. Access Token — Azure AD client-credentials OAuth (login.microsoftonline.com)
 *   2. Embed Token  — Power BI GenerateToken for the report (parses group/report id)
 *   3. Report HTML  — fetch the report HTML with the embed token
 *
 * The real calls must run server-side (client secret + Microsoft CORS), so this
 * will POST the 4 credentials to a staff-api resolver (`staffPowerbiVerify`,
 * mirroring the original `powerbi-troubleshoot` edge function) and render
 * whatever it returns. Frontend only for now — "Run" populates DEMO data so the
 * full per-step UI is reviewable; group/report id parsing is real. The result
 * shape matches the planned backend contract, so wiring is a drop-in.
 */
import { useState } from 'react';
import { ChevronDown, ChevronRight, Eye, EyeOff } from 'lucide-react';
import { Button, Input, Label } from '@staff-app/ui';
import { cn } from '@/lib/utils';
import { usePowerbiVerify, type PowerbiStep } from '../hooks/usePowerbiVerify';

const GUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

type FieldKey = 'reportUrl' | 'clientId' | 'clientSecret' | 'tenantId';

interface FieldDef {
  key: FieldKey;
  label: string;
  placeholder: string;
  type?: 'text' | 'password';
  validate?: (v: string) => boolean;
  helper?: string;
}

const FIELDS: FieldDef[] = [
  {
    key: 'reportUrl',
    label: 'Report URL',
    placeholder: 'https://app.powerbi.com/reportEmbed?reportId=...',
    validate: (v) => /^https?:\/\//i.test(v),
    helper: 'The embed URL from Power BI (app.powerbi.com/reportEmbed?reportId=…&groupId=…).',
  },
  {
    key: 'clientId',
    label: 'Client ID',
    placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
    validate: (v) => GUID_RE.test(v),
    helper: 'Azure AD application (client) ID — a GUID.',
  },
  { key: 'clientSecret', label: 'Client Secret', placeholder: 'Enter client secret', type: 'password' },
  {
    key: 'tenantId',
    label: 'Tenant ID',
    placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
    validate: (v) => GUID_RE.test(v),
    helper: 'Azure AD directory (tenant) ID — a GUID.',
  },
];

type StepKey = 'accessToken' | 'embedToken' | 'reportHtml';
type StepOutcome = 'success' | 'failed' | 'blocked';

/** Matches the planned `staffPowerbiVerify` per-step response. */
interface StepResult {
  outcome: StepOutcome;
  requestUrl?: string;
  httpStatus?: number;
  durationMs?: number;
  token?: string; // steps 1 & 2 — masked w/ reveal toggle
  groupId?: string; // step 2
  reportId?: string; // step 2
  htmlPreview?: string; // step 3
  headers?: Record<string, string>; // step 3
  error?: string;
  raw?: string; // collapsible raw response
}

const STEPS: { key: StepKey; label: string }[] = [
  { key: 'accessToken', label: 'Step 1 — Access Token' },
  { key: 'embedToken', label: 'Step 2 — Embed Token' },
  { key: 'reportHtml', label: 'Step 3 — Report HTML' },
];

const EMPTY_FORM: Record<FieldKey, string> = { reportUrl: '', clientId: '', clientSecret: '', tenantId: '' };

function fieldValid(field: FieldDef, raw: string): boolean {
  const v = raw.trim();
  if (!v) return false;
  return field.validate ? field.validate(v) : true;
}

/** Map a staffPowerbiVerify step (nullable fields, headersJson) to the UI shape. */
function toStep(s: PowerbiStep): StepResult {
  let headers: Record<string, string> | undefined;
  if (s.headersJson) {
    try {
      headers = JSON.parse(s.headersJson) as Record<string, string>;
    } catch {
      /* ignore malformed */
    }
  }
  return {
    outcome: s.outcome,
    requestUrl: s.requestUrl ?? undefined,
    httpStatus: s.httpStatus ?? undefined,
    durationMs: s.durationMs ?? undefined,
    token: s.token ?? undefined,
    groupId: s.groupId ?? undefined,
    reportId: s.reportId ?? undefined,
    htmlPreview: s.htmlPreview ?? undefined,
    headers,
    error: s.error ?? undefined,
    raw: s.raw ?? undefined,
  };
}

function badgeClass(outcome: StepOutcome): string {
  switch (outcome) {
    case 'success':
      return 'bg-success/10 text-success';
    case 'failed':
      return 'bg-destructive/10 text-destructive';
    case 'blocked':
      return 'bg-warning/10 text-warning';
  }
}

function badgeLabel(outcome: StepOutcome): string {
  return outcome === 'success' ? '✅ Success' : outcome === 'failed' ? '❌ Failed' : '⚠️ Blocked';
}

function maskToken(token: string): string {
  if (token.length <= 14) return '•'.repeat(token.length);
  return `${token.slice(0, 8)}${'•'.repeat(12)}${token.slice(-6)}`;
}

function TokenViewer({ token }: { token: string }) {
  const [show, setShow] = useState(false);
  return (
    <div className="flex items-start gap-2">
      <code className="block flex-1 break-all rounded bg-muted px-2 py-1 text-[11px] text-foreground">
        {show ? token : maskToken(token)}
      </code>
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={() => setShow((s) => !s)}
        className="cursor-pointer"
        aria-label={show ? 'Hide token' : 'Reveal token'}
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </Button>
    </div>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="text-xs text-foreground">{value}</dd>
    </div>
  );
}

function StepCard({ label, result }: { label: string; result: StepResult }) {
  const [open, setOpen] = useState(true);
  const [rawOpen, setRawOpen] = useState(false);
  return (
    <div className="rounded-md border bg-card" data-testid={`powerbi-step-${label}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full cursor-pointer items-center justify-between gap-2 px-3 py-2 text-left"
      >
        <span className="flex items-center gap-2">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <span className="text-sm font-medium text-foreground">{label}</span>
        </span>
        <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', badgeClass(result.outcome))}>
          {badgeLabel(result.outcome)}
        </span>
      </button>

      {open ? (
        <div className="flex flex-col gap-3 border-t px-3 py-3">
          <dl className="grid grid-cols-2 gap-3">
            {result.requestUrl ? <KV label="Request URL" value={<code className="break-all">{result.requestUrl}</code>} /> : null}
            {result.httpStatus != null ? <KV label="HTTP Status" value={result.httpStatus} /> : null}
            {result.durationMs != null ? <KV label="Timing" value={`${result.durationMs} ms`} /> : null}
            {result.groupId ? <KV label="Group ID" value={<code>{result.groupId}</code>} /> : null}
            {result.reportId ? <KV label="Report ID" value={<code>{result.reportId}</code>} /> : null}
          </dl>

          {result.token ? (
            <div className="flex flex-col gap-1">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Token</span>
              <TokenViewer token={result.token} />
            </div>
          ) : null}

          {result.headers ? (
            <div className="flex flex-col gap-1">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Response Headers</span>
              <dl className="rounded bg-muted px-2 py-1 text-[11px]">
                {Object.entries(result.headers).map(([k, v]) => (
                  <div key={k} className="flex gap-2">
                    <dt className="font-medium text-muted-foreground">{k}:</dt>
                    <dd className="break-all text-foreground">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ) : null}

          {result.htmlPreview ? (
            <div className="flex flex-col gap-1">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">HTML Preview</span>
              <pre className="max-h-40 overflow-auto rounded bg-muted px-2 py-1 text-[11px] text-foreground">{result.htmlPreview}</pre>
            </div>
          ) : null}

          {result.error ? (
            <p className="rounded border border-destructive/30 bg-destructive/5 px-2 py-1 text-xs text-destructive">{result.error}</p>
          ) : null}

          {result.raw ? (
            <div>
              <button
                type="button"
                onClick={() => setRawOpen((o) => !o)}
                className="flex cursor-pointer items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
              >
                {rawOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                {'Raw response'}
              </button>
              {rawOpen ? (
                <pre className="mt-1 max-h-48 overflow-auto rounded bg-muted px-2 py-1 text-[11px] text-foreground">{result.raw}</pre>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function PowerBiAuthConsole() {
  const [form, setForm] = useState<Record<FieldKey, string>>(EMPTY_FORM);
  const [results, setResults] = useState<Partial<Record<StepKey, StepResult>> | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [runVerify, { loading: busy }] = usePowerbiVerify();

  const valid = FIELDS.every((f) => fieldValid(f, form[f.key] ?? ''));

  const set = (key: FieldKey) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((s) => ({ ...s, [key]: e.target.value }));

  const handleRun = async () => {
    if (!valid || busy) return;
    setNotice(null);
    setResults(null);
    try {
      const res = await runVerify({
        variables: {
          reportUrl: form.reportUrl.trim(),
          clientId: form.clientId.trim(),
          clientSecret: form.clientSecret,
          tenantId: form.tenantId.trim(),
        },
      });
      const r = res.data?.staffPowerbiVerify;
      if (!r) {
        setNotice(res.error?.message ?? 'Verification failed — no response.');
        return;
      }
      // Early-exit: embedToken/reportHtml are null when a prior step didn't pass.
      setResults({
        accessToken: toStep(r.accessToken),
        ...(r.embedToken ? { embedToken: toStep(r.embedToken) } : {}),
        ...(r.reportHtml ? { reportHtml: toStep(r.reportHtml) } : {}),
      });
    } catch (e) {
      setNotice((e as Error)?.message ?? 'Verification request failed.');
    }
  };

  const handleReset = () => {
    setForm(EMPTY_FORM);
    setResults(null);
    setNotice(null);
  };

  return (
    <div className="flex flex-col gap-5 rounded-lg border bg-card p-5 shadow-sm md:max-w-2xl" data-testid="powerbi-auth-console">
      <div className="flex flex-col gap-1">
        <h3 className="text-base font-semibold text-foreground">{'Power BI Auth Verification'}</h3>
        <p className="text-sm font-medium text-muted-foreground">{'Authentication Troubleshooting'}</p>
        <p className="text-xs text-muted-foreground">{'Verify access token, embed token, and report HTML retrieval'}</p>
      </div>

      <div className="flex flex-col gap-4">
        {FIELDS.map((field) => {
          const value = form[field.key] ?? '';
          const invalid = value.trim().length > 0 && !fieldValid(field, value);
          return (
            <div key={field.key} className="flex flex-col gap-1.5">
              <Label htmlFor={`powerbi-${field.key}`} className="text-xs">
                {field.label} <span className="text-destructive">{'*'}</span>
              </Label>
              <Input
                id={`powerbi-${field.key}`}
                type={field.type === 'password' ? 'password' : 'text'}
                value={value}
                onChange={set(field.key)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && valid && !busy) handleRun();
                }}
                placeholder={field.placeholder}
                autoComplete={field.type === 'password' ? 'new-password' : 'off'}
                aria-invalid={invalid}
                className={invalid ? 'border-destructive' : undefined}
                data-testid={`powerbi-${field.key}`}
              />
              {field.helper ? <p className="text-[11px] text-muted-foreground">{field.helper}</p> : null}
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-2">
        <Button onClick={handleRun} disabled={!valid || busy} className="cursor-pointer" data-testid="powerbi-run">
          {busy ? 'Running…' : 'Run Troubleshooting'}
        </Button>
        <Button variant="outline" onClick={handleReset} disabled={busy} className="cursor-pointer" data-testid="powerbi-reset">
          {'Reset'}
        </Button>
      </div>

      {notice ? (
        <p className="text-xs text-muted-foreground" data-testid="powerbi-notice">{notice}</p>
      ) : null}

      {results ? (
        <div className="flex flex-col gap-3 border-t pt-4" data-testid="powerbi-results">
          {STEPS.map((step) => {
            const r = results[step.key];
            return r ? <StepCard key={step.key} label={step.label} result={r} /> : null;
          })}
        </div>
      ) : null}
    </div>
  );
}
